import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, File, FileText, Image, Video, Award, GraduationCap, BookOpen,
  Download, Loader2, Wand2, Lightbulb, ListChecks, Copy, Check, FolderOpen,
  AlertCircle, Trash2, ChevronDown, ChevronRight, Eye, EyeOff, Globe,
  Users, Bookmark, Heart, Star, Code, FileSpreadsheet, Presentation,
  BarChart, FileImage, Film, Trophy, Newspaper, GripVertical, ArrowRightLeft
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import resumeAPI from '../services/api';
import { extractTextFromFile, truncateForAI } from '../utils/documentTextExtractor';
import { generateBulletsFromEvaluation, extractSkillsFromDocument, summarizeDocument } from '../services/aiService';

// ── 16 categories in display order ──
const ALL_CATEGORIES = [
  { id: 'word', label: 'Word Documents', icon: FileText, color: 'blue' },
  { id: 'excel', label: 'Excel Spreadsheets', icon: FileSpreadsheet, color: 'green' },
  { id: 'powerpoint', label: 'PowerPoint Presentations', icon: Presentation, color: 'orange' },
  { id: 'images', label: 'Pictures/Images', icon: FileImage, color: 'purple' },
  { id: 'videos', label: 'Videos', icon: Film, color: 'red' },
  { id: 'awards', label: 'Awards', icon: Trophy, color: 'yellow' },
  { id: 'certificates', label: 'Certificates', icon: GraduationCap, color: 'indigo' },
  { id: 'projects', label: 'Projects', icon: Code, color: 'pink' },
  { id: 'leadership', label: 'Leadership & Team Development', icon: Users, color: 'emerald' },
  { id: 'case_studies', label: 'Case Studies', icon: Bookmark, color: 'violet' },
  { id: 'community_service', label: 'Community & Service', icon: Heart, color: 'rose' },
  { id: 'performance_evaluations', label: 'Performance Evaluations', icon: Star, color: 'sky' },
  { id: 'publications', label: 'Publications', icon: Newspaper, color: 'teal' },
  { id: 'training', label: 'Training Materials', icon: BookOpen, color: 'cyan' },
  { id: 'reports', label: 'Reports & Analysis', icon: BarChart, color: 'amber' },
  { id: 'other', label: 'Other', icon: File, color: 'gray' },
];

const PortfolioFilesPage = () => {
  const { isAuthenticated } = useAuth();
  const [primaryResume, setPrimaryResume] = useState(null);
  const [portfolioFiles, setPortfolioFiles] = useState([]);
  const [loading, setLoading] = useState(true);

  // Folder expand/collapse state — keyed by category id
  const [expandedFolders, setExpandedFolders] = useState({});

  // AI states per file
  const [aiActionLoading, setAiActionLoading] = useState({});
  const [aiErrors, setAiErrors] = useState({});
  const [extractedSkills, setExtractedSkills] = useState({});
  const [generatedBullets, setGeneratedBullets] = useState({});
  const [copiedBullet, setCopiedBullet] = useState(null);

  // Drag-and-drop state
  const [draggedFileId, setDraggedFileId] = useState(null);
  const [dragOverFolder, setDragOverFolder] = useState(null);

  const AI_SUPPORTED_EXTENSIONS = ['doc', 'docx', 'pdf', 'pptx', 'ppt', 'xlsx', 'xls', 'txt', 'md', 'csv'];

  const isAiSupported = (fileObj) => {
    const ext = (fileObj.name || '').split('.').pop().toLowerCase();
    return AI_SUPPORTED_EXTENSIONS.includes(ext);
  };

  // ── Load ──
  useEffect(() => { loadFiles(); }, []);

  const loadFiles = async () => {
    setLoading(true);
    try {
      // Load portfolio files from user profile (separate from resumes)
      const profile = await resumeAPI.getMyProfile();
      if (profile) {
        setPrimaryResume(profile);
        const files = profile.portfolio_files || [];
        setPortfolioFiles(files);

        // Restore persisted AI results
        const skills = {};
        const bullets = {};
        files.forEach(f => {
          if (f.extracted_skills && f.extracted_skills.length > 0) skills[f.id] = f.extracted_skills;
          if (f.generated_bullets && f.generated_bullets.length > 0) bullets[f.id] = f.generated_bullets;
        });
        setExtractedSkills(skills);
        setGeneratedBullets(bullets);

        // Auto-expand folders that have files
        const hasFiles = {};
        files.forEach(f => { hasFiles[f.category || 'other'] = true; });
        setExpandedFolders(hasFiles);
      }
    } catch (err) {
      console.error('Failed to load portfolio files:', err);
    } finally {
      setLoading(false);
    }
  };

  // ── Persist portfolio files to user profile (NOT to any resume) ──
  const persistFiles = async (files) => {
    try {
      await resumeAPI.updateMyProfile({ portfolio_files: files });
    } catch (err) {
      console.error('[PortfolioFiles] Failed to persist:', err);
    }
  };

  // ── Delete ──
  const handleDeleteFile = async (fileId, fileName) => {
    if (!window.confirm(`Are you sure you want to delete "${fileName}"? This cannot be undone.`)) return;
    const updated = portfolioFiles.filter(f => f.id !== fileId);
    setPortfolioFiles(updated);
    setExtractedSkills(prev => { const n = { ...prev }; delete n[fileId]; return n; });
    setGeneratedBullets(prev => { const n = { ...prev }; delete n[fileId]; return n; });
    setAiErrors(prev => { const n = { ...prev }; delete n[fileId]; return n; });
    await persistFiles(updated);
  };

  // ── Toggle public visibility ──
  const handleTogglePublic = async (fileId) => {
    const updated = portfolioFiles.map(f =>
      f.id === fileId ? { ...f, is_public: !f.is_public } : f
    );
    setPortfolioFiles(updated);
    await persistFiles(updated);
  };

  // ── Folder expand/collapse ──
  const toggleFolder = (categoryId) => {
    setExpandedFolders(prev => ({ ...prev, [categoryId]: !prev[categoryId] }));
  };

  // ── Change file category (dropdown) ──
  const handleChangeCategory = async (fileId, newCategory) => {
    const updated = portfolioFiles.map(f =>
      f.id === fileId ? { ...f, category: newCategory } : f
    );
    setPortfolioFiles(updated);
    // Auto-expand the target folder so the user sees the moved file
    setExpandedFolders(prev => ({ ...prev, [newCategory]: true }));
    await persistFiles(updated);
  };

  // ── Drag-and-drop between folders ──
  const handleFileDragStart = (e, fileId) => {
    setDraggedFileId(fileId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleFolderDragOver = (e, categoryId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverFolder(categoryId);
  };

  const handleFolderDragLeave = () => {
    setDragOverFolder(null);
  };

  const handleFolderDrop = async (e, targetCategoryId) => {
    e.preventDefault();
    setDragOverFolder(null);
    if (!draggedFileId) return;
    const file = portfolioFiles.find(f => f.id === draggedFileId);
    if (file && file.category !== targetCategoryId) {
      await handleChangeCategory(draggedFileId, targetCategoryId);
    }
    setDraggedFileId(null);
  };

  const handleDragEnd = () => {
    setDraggedFileId(null);
    setDragOverFolder(null);
  };

  // ── AI Handlers ──
  const fetchFileAsBlob = async (url, fileName) => {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Failed to download file: ${response.status}`);
    const blob = await response.blob();
    return new window.File([blob], fileName, { type: blob.type });
  };

  const handleAiAction = async (fileObj, action) => {
    setAiActionLoading(prev => ({ ...prev, [fileObj.id]: action }));
    setAiErrors(prev => ({ ...prev, [fileObj.id]: null }));
    try {
      if (!fileObj.url) throw new Error('File is not stored in cloud. Please re-upload from the Portfolio page.');
      const file = await fetchFileAsBlob(fileObj.url, fileObj.name);
      const rawText = await extractTextFromFile(file);
      if (!rawText || rawText.length < 20) {
        throw new Error('Could not extract enough text from this file for AI analysis.');
      }
      const text = truncateForAI(rawText, 3000);

      if (action === 'summarize') {
        const summary = await summarizeDocument(text, fileObj.name);
        const updated = portfolioFiles.map(f => f.id === fileObj.id ? { ...f, summary } : f);
        setPortfolioFiles(updated);
        await persistFiles(updated);
      } else if (action === 'skills') {
        const skills = await extractSkillsFromDocument(text, []);
        setExtractedSkills(prev => ({ ...prev, [fileObj.id]: skills }));
        const updated = portfolioFiles.map(f => f.id === fileObj.id ? { ...f, extracted_skills: skills } : f);
        setPortfolioFiles(updated);
        await persistFiles(updated);
      } else if (action === 'bullets') {
        const bullets = await generateBulletsFromEvaluation(text);
        setGeneratedBullets(prev => ({ ...prev, [fileObj.id]: bullets }));
        const updated = portfolioFiles.map(f => f.id === fileObj.id ? { ...f, generated_bullets: bullets } : f);
        setPortfolioFiles(updated);
        await persistFiles(updated);
      }
    } catch (err) {
      console.error(`[AI ${action}]`, err);
      setAiErrors(prev => ({ ...prev, [fileObj.id]: err.message }));
    } finally {
      setAiActionLoading(prev => ({ ...prev, [fileObj.id]: null }));
    }
  };

  const handleCopyBullet = (fileId, index, text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedBullet(`${fileId}-${index}`);
      setTimeout(() => setCopiedBullet(null), 2000);
    });
  };

  // ── Helpers ──
  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const filesByCategory = portfolioFiles.reduce((acc, file) => {
    const cat = file.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(file);
    return acc;
  }, {});

  const publicCount = portfolioFiles.filter(f => f.is_public).length;

  // ── Render ──
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading portfolio files...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container-custom py-8">
        {/* Back Navigation */}
        <div className="mb-6 flex items-center justify-between">
          <Link
            to="/portfolio"
            className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Portfolio
          </Link>
          {primaryResume?.public_link_id && (
            <Link
              to={`/portfolio/public/${primaryResume.public_link_id}`}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-teal-600 text-white hover:bg-teal-700 transition-colors shadow-sm"
            >
              <Globe className="w-4 h-4" />
              Portfolio (Public View)
            </Link>
          )}
        </div>

        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
            <FolderOpen className="w-8 h-8 text-primary-600" />
            My Portfolio Files
          </h1>
          <p className="text-gray-600">
            Your stored portfolio documents, organized into 16 category folders. Use AI tools to analyze files, and mark files as public to share them.
          </p>
          <div className="flex items-center gap-4 mt-2">
            <span className="text-sm text-gray-500">
              {portfolioFiles.length} file{portfolioFiles.length !== 1 ? 's' : ''} stored
            </span>
            {publicCount > 0 && (
              <span className="text-sm text-teal-600 flex items-center gap-1">
                <Eye className="w-3.5 h-3.5" />
                {publicCount} public
              </span>
            )}
          </div>
        </div>

        {/* 16 Folder Cards */}
        <div className="space-y-3">
          {ALL_CATEGORIES.map(cat => {
            const files = filesByCategory[cat.id] || [];
            const isExpanded = !!expandedFolders[cat.id];
            const IconComponent = cat.icon;
            const folderPublicCount = files.filter(f => f.is_public).length;

            const isDragTarget = dragOverFolder === cat.id;

            return (
              <div
                key={cat.id}
                className={`bg-white rounded-xl border shadow-sm overflow-hidden transition-colors ${
                  isDragTarget ? 'border-primary-400 bg-primary-50 ring-2 ring-primary-200' : 'border-gray-200'
                }`}
                onDragOver={(e) => handleFolderDragOver(e, cat.id)}
                onDragLeave={handleFolderDragLeave}
                onDrop={(e) => handleFolderDrop(e, cat.id)}
              >
                {/* Folder Header */}
                <button
                  onClick={() => toggleFolder(cat.id)}
                  className={`w-full flex items-center gap-3 px-5 py-4 hover:bg-gray-50 transition-colors text-left ${
                    isDragTarget ? 'bg-primary-50' : ''
                  }`}
                >
                  {isExpanded ? (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                  <div className="bg-primary-100 p-2 rounded-lg flex-shrink-0">
                    <IconComponent className="w-5 h-5 text-primary-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="font-semibold text-gray-900">{cat.label}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {folderPublicCount > 0 && (
                      <span className="text-xs text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full flex items-center gap-1">
                        <Eye className="w-3 h-3" /> {folderPublicCount}
                      </span>
                    )}
                    <span className="text-sm text-gray-500 bg-gray-100 px-2.5 py-0.5 rounded-full">
                      {files.length} file{files.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                </button>

                {/* Folder Content (expanded) */}
                {isExpanded && (
                  <div className="border-t border-gray-100 px-5 py-4">
                    {files.length === 0 ? (
                      <p className="text-sm text-gray-400 italic py-4 text-center">No files in this folder. Upload files from the Portfolio page.</p>
                    ) : (
                      <div className="space-y-4">
                        {files.map(fileObj => {
                          const fileAiSupported = isAiSupported(fileObj);
                          const loadingAction = aiActionLoading[fileObj.id];
                          const fileError = aiErrors[fileObj.id];
                          const fileSkills = extractedSkills[fileObj.id];
                          const fileBullets = generatedBullets[fileObj.id];

                          return (
                            <div
                              key={fileObj.id}
                              draggable
                              onDragStart={(e) => handleFileDragStart(e, fileObj.id)}
                              onDragEnd={handleDragEnd}
                              className={`border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-all ${
                                draggedFileId === fileObj.id ? 'opacity-40' : ''
                              }`}
                            >
                              {/* File header */}
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex items-start gap-3 flex-1 min-w-0">
                                  <GripVertical className="w-4 h-4 text-gray-400 mt-1.5 cursor-grab flex-shrink-0" />
                                  <div className="bg-gray-100 p-2 rounded-lg mt-0.5 flex-shrink-0">
                                    <IconComponent className="w-4 h-4 text-gray-600" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-gray-900 truncate">{fileObj.name}</p>
                                    <p className="text-sm text-gray-500">
                                      {formatFileSize(fileObj.size)}
                                      {fileObj.uploadedAt && ` \u2022 Uploaded ${new Date(fileObj.uploadedAt).toLocaleDateString()}`}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                                  {/* Move to category dropdown */}
                                  {isAuthenticated && (
                                    <select
                                      value={fileObj.category || 'other'}
                                      onChange={(e) => handleChangeCategory(fileObj.id, e.target.value)}
                                      className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-600 hover:border-gray-300 focus:ring-1 focus:ring-primary-300 focus:border-primary-300 cursor-pointer"
                                      title="Move to another folder"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {ALL_CATEGORIES.map(c => (
                                        <option key={c.id} value={c.id}>{c.label}</option>
                                      ))}
                                    </select>
                                  )}
                                  {/* Public toggle */}
                                  {isAuthenticated && (
                                    <button
                                      onClick={() => handleTogglePublic(fileObj.id)}
                                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                                        fileObj.is_public
                                          ? 'bg-teal-50 text-teal-700 border-teal-200 hover:bg-teal-100'
                                          : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                                      }`}
                                      title={fileObj.is_public ? 'Click to hide from public view' : 'Click to make visible in public view'}
                                    >
                                      {fileObj.is_public ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                                      {fileObj.is_public ? 'Public' : 'Private'}
                                    </button>
                                  )}
                                  {/* Open file */}
                                  {fileObj.url && (
                                    <a
                                      href={fileObj.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                                    >
                                      <Download className="w-3.5 h-3.5" />
                                      Open
                                    </a>
                                  )}
                                  {/* Delete */}
                                  {isAuthenticated && (
                                    <button
                                      onClick={() => handleDeleteFile(fileObj.id, fileObj.name)}
                                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg text-red-600 hover:bg-red-50 border border-red-200 hover:border-red-300 transition-colors"
                                      title="Delete file"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                </div>
                              </div>

                              {/* Summary */}
                              {fileObj.summary && (
                                <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                                  <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">AI Summary</h4>
                                  <p className="text-sm text-gray-700 leading-relaxed">{fileObj.summary}</p>
                                </div>
                              )}

                              {/* AI Action Buttons */}
                              {isAuthenticated && fileAiSupported && fileObj.url && (
                                <div className="pt-3 border-t border-gray-100">
                                  <div className="flex flex-wrap items-center gap-2 mb-2">
                                    <button
                                      type="button"
                                      onClick={() => handleAiAction(fileObj, 'summarize')}
                                      disabled={!!loadingAction}
                                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-purple-50 text-purple-700 hover:bg-purple-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                      {loadingAction === 'summarize' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                                      {loadingAction === 'summarize' ? 'Summarizing...' : 'AI Summarize'}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleAiAction(fileObj, 'skills')}
                                      disabled={!!loadingAction}
                                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-purple-50 text-purple-700 hover:bg-purple-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                      {loadingAction === 'skills' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Lightbulb className="w-3.5 h-3.5" />}
                                      {loadingAction === 'skills' ? 'Extracting...' : 'Extract Skills'}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleAiAction(fileObj, 'bullets')}
                                      disabled={!!loadingAction}
                                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-purple-50 text-purple-700 hover:bg-purple-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                      {loadingAction === 'bullets' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ListChecks className="w-3.5 h-3.5" />}
                                      {loadingAction === 'bullets' ? 'Generating...' : 'Generate Bullets'}
                                    </button>
                                  </div>

                                  {fileError && (
                                    <div className="mt-2 p-2 bg-red-50 rounded-md flex items-start gap-2">
                                      <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                                      <p className="text-xs text-red-600">{fileError}</p>
                                    </div>
                                  )}

                                  {fileSkills && fileSkills.length > 0 && (
                                    <div className="mt-3 p-3 bg-purple-50 rounded-lg">
                                      <h5 className="text-xs font-semibold text-purple-800 mb-2 flex items-center gap-1">
                                        <Lightbulb className="w-3.5 h-3.5" />
                                        Extracted Skills ({fileSkills.length})
                                      </h5>
                                      <div className="flex flex-wrap gap-1.5">
                                        {fileSkills.map((skill, idx) => (
                                          <span key={idx} className="inline-flex items-center px-2.5 py-1 text-xs font-medium bg-white text-purple-700 rounded-full border border-purple-200">
                                            {skill}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {fileBullets && fileBullets.length > 0 && (
                                    <div className="mt-3 p-3 bg-purple-50 rounded-lg">
                                      <h5 className="text-xs font-semibold text-purple-800 mb-2 flex items-center gap-1">
                                        <ListChecks className="w-3.5 h-3.5" />
                                        Resume Bullet Points ({fileBullets.length})
                                      </h5>
                                      <ul className="space-y-2">
                                        {fileBullets.map((bullet, idx) => (
                                          <li key={idx} className="flex items-start gap-2 group">
                                            <span className="text-purple-400 mt-0.5 text-xs">&bull;</span>
                                            <span className="text-xs text-gray-800 flex-1">{bullet}</span>
                                            <button
                                              type="button"
                                              onClick={() => handleCopyBullet(fileObj.id, idx, bullet)}
                                              className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-purple-100"
                                              title="Copy bullet"
                                            >
                                              {copiedBullet === `${fileObj.id}-${idx}` ? (
                                                <Check className="w-3.5 h-3.5 text-green-600" />
                                              ) : (
                                                <Copy className="w-3.5 h-3.5 text-purple-500" />
                                              )}
                                            </button>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PortfolioFilesPage;
